/* eslint-disable new-cap */
import {LitElement, html, nothing} from 'lit';
import {MobxReactionUpdate} from '@adobe/lit-mobx';
import {classMap} from 'lit/directives/class-map.js';
import styles from './facet-basic.component.scss?as=stylesheet';
import {createRef, ref} from 'lit/directives/ref.js';
import {defineCustomElement, roundToIncrement} from 'extensions/utils';

export class FacetBasic extends MobxReactionUpdate(LitElement) {
	static get styles () {
		return [styles];
	}

	static get properties () {
		return {
			apiEndpoint: {
				attribute: 'api-endpoint',
				type: String
			},
			facet: {
				type: Object
			},
			service: {
				state: true,
				type: Object
			}
		};
	}

	sliderInputs = createRef();

	get active () {
		// eslint-disable-next-line no-extra-parens
		return this.facet?.values?.some(value => value.selected) || (this.facet?.range?.min !== this.facet?.range?.start || this.facet?.range?.max !== this.facet?.range?.end);
	}

	constructor () {
		super();

		/**
		 * @type {string}
		 */
		this.apiEndpoint = '';

		/**
		 * @type {import('extensions/hawksearch/services/types').Facet}
		 */
		this.facet = {};

		/**
		 * @type {import('extensions/hawksearch/services/search-service').SearchService}
		 */
		this.service = {};
	}

	toggleCollapsed () {
		this.service?.store?.setFacetCollapsed?.(this.facet, this.facet.collapsible && !this.facet.collapsed);
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').FacetValue} facetValue
	 */
	handleLinkClick (facetValue) {
		if (this.facet?.expandSelection) {
			return this.service.toggleFacetValue(this.facet?.field, facetValue?.value);
		}

		if (facetValue?.selected) {
			return this.service.clearFacet(this.facet?.field);
		}

		return this.service.setFacetValue(this.facet?.field, facetValue?.value);
	}

	renderFacetHeader () {
		return html`
			<div class="facet-header">
				<div class="facet-header--title">
					<button class="facet-header--label" @click="${this.toggleCollapsed}">
						${this.facet?.title}<span class="icon-caret--down facet-header--label-icon" aria-hidden="true"></span>
					</button>
					${this.active ? html`<a class="facet-reset-button" @click="${() => this.service?.clearFacet(this.facet?.field)}"><span data-icon="" aria-hidden="true"></span></a>` : nothing}
					${this.facet?.expandSelection ? html`<span class="facet-header--multiple">You may select multiple</span>` : nothing}
				</div>
				${this.facet?.custom?.excludeTooltip ? nothing : html`
					<rb-tooltip class="facet-header--tooltip" code="${this.facet?.field}" title="${this.facet?.title}" endpoint="${this.apiEndpoint}"></rb-tooltip>
				`}
			</div>
		`;
	}

	renderFacetValues () {
		return this.facet?.values?.map(facetValue => html`
			<div class="facet--value facet-value-default">
				${this.renderFacetValue(facetValue)}
			</div>`);
	}

	/**
	 * @param {import('extensions/hawksearch/services/types').FacetValue} facetValue
	 */
	renderFacetValue (facetValue) {
		const classes = {
			'facet--value-active': facetValue.selected,
			'facet--value-checkbox': this.facet?.expandSelection,
			'facet--value-link': true
		};

		return html`
			<a class="${classMap(classes)}" @click="${() => this.handleLinkClick(facetValue)}">${facetValue.title}</a>
		`;
	}

	handleChange (formattedValues, handleNumber, values) {
		// Format as an int if the step is a whole number
		const [
			low,
			high
		] = values
			.map(value => roundToIncrement(value, this.facet?.custom?.step));

		// eslint-disable-next-line eqeqeq
		if (low == this.facet.range.min && high == this.facet.range.max) {
			this.service.clearFacet(this.facet.field);
		}
		else {
			this.service.setFacetValue(this.facet.field, `${low},${high}`);
		}
	}

	handleSlide (formattedValues, handleNumber, values) {
		// Format as an int if the step is a whole number
		const [
			low,
			high
		] = values
			.map(value => roundToIncrement(value, this.facet?.custom?.step));

		// Set webcomponent attributes if the slider inputs component exists
		if (this.sliderInputs?.value) {
			this.sliderInputs.value.high = high;
			this.sliderInputs.value.low = low;
		}
	}

	renderRangeInputs () {
		return this.facet?.range?.showInputs
			? html`<rb-facet-slider-inputs ${ref(this.sliderInputs)} facet="${this.facet?.field}" step="${this.facet?.custom?.step}" format="${this.facet?.range?.type}" ?disableFormatOverlay="${!this.facet?.range?.type}" .handleChange="${this.handleChange.bind(this)}" high="${this.facet?.range?.end}" low="${this.facet?.range?.start}" label="${this.facet?.custom?.sliderInputLabel || this.facet?.title}"></rb-facet-slider-inputs>`
			: nothing;
	}

	render () {
		const classes = {
			facet: true,
			'is-closed': this.facet?.collapsible && this.facet?.collapsed,
			[`facet-field--${this.facet.field}`]: true,
			[`facet-type--${this.facet.type}`]: true
		};

		return html`
			<div class="${classMap(classes)}">
				${this.renderFacetHeader()}
				<div class="facet--values">
					${this.renderRangeInputs()}
					${this.renderFacetValues()}
				</div>
			</div>
		`;
	}
};

defineCustomElement('rb-facet-basic', FacetBasic);
